import { useCallback, useMemo } from 'react'
import {
  useLocation as useRRLocation,
  useNavigate as useRRNavigate,
  useParams as useRRParams,
  useSearchParams as useRRSearchParams,
} from 'react-router-dom'

import type { AppLocation, AppNavigateFn, AppNavigateOptions, RouterAdapter } from '../types'

export function createReactRouterAdapter(): RouterAdapter {
  return {
    useNavigate: () => {
      const navigate = useRRNavigate()
      return useMemo(() => {
        const fn = (toOrDelta: string | Partial<AppLocation> | number, options?: AppNavigateOptions): void => {
          if (typeof toOrDelta === 'number') {
            navigate(toOrDelta)
          } else {
            navigate(toOrDelta, options)
          }
        }
        return fn as AppNavigateFn
      }, [navigate])
    },
    useParams: <T extends Record<string, string | undefined>>() => {
      return useRRParams() as T
    },
    useLocation: () => {
      const { pathname, search, hash } = useRRLocation()
      return useMemo(() => ({ pathname, search, hash }), [pathname, search, hash])
    },
    useSearchParams: () => {
      const [params, setParams] = useRRSearchParams()
      const normalizedSet = useCallback(
        (next: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams)) => {
          setParams(next)
        },
        [setParams],
      )
      return [params, normalizedSet]
    },
  }
}
